import { isSameMonth, startOfMonth } from 'date-fns';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import CalendarView from '~/components/calendar/CalendarView';
import {
  CalendarLayout,
  useCalendarContext,
} from '~/components/layout/CalendarLayout';

// Inner component that uses CalendarContext for range extension
function CalendarViewWithRangeExtension({
  placeId,
  jobId,
  currentMonth,
  onJobClick,
  onMonthChange,
  scrollToTodayRef,
  scrollToMonthRef,
}: {
  placeId: string;
  jobId?: string;
  currentMonth: Date;
  onJobClick: (jobId: string) => void;
  onMonthChange: (month: Date) => void;
  scrollToTodayRef: React.MutableRefObject<(() => void) | null>;
  scrollToMonthRef: React.MutableRefObject<((month: Date) => void) | null>;
}) {
  const { ensureDateInRange } = useCalendarContext();

  // Handle month changes and ensure the range is loaded
  // ensureDateInRange handles both incremental extensions (for scrolling)
  // and re-centering (for large jumps like picking a far-away month)
  const handleMonthChangeWithExtension = useCallback(
    (month: Date) => {
      // Ensure data is loaded for this month
      ensureDateInRange?.(month);

      // Always notify parent of month change
      onMonthChange(month);
    },
    [ensureDateInRange, onMonthChange]
  );

  return (
    <CalendarView
      placeId={placeId}
      selectedJobId={jobId}
      currentMonth={currentMonth}
      onJobClick={onJobClick}
      onMonthChange={handleMonthChangeWithExtension}
      scrollToTodayRef={scrollToTodayRef}
      scrollToMonthRef={scrollToMonthRef}
    />
  );
}

export default function CalendarRoute() {
  const { placeId, jobId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Compute initial month from URL date parameter if present, otherwise use today
  const computeMonthFromURL = useCallback(() => {
    const dateParam = searchParams.get('date');
    if (dateParam) {
      try {
        const parsedDate = new Date(dateParam);
        // Validate that the parsed date is valid
        if (!isNaN(parsedDate.getTime())) {
          const result = startOfMonth(parsedDate);
          return result;
        }
      } catch {
        // Fall through to default
      }
    }
    const today = startOfMonth(new Date());
    return today;
  }, [searchParams]);

  // Capture initial month ONCE from URL - never react to URL changes after mount
  const [currentMonth, setCurrentMonth] = useState<Date>(computeMonthFromURL());
  const scrollToTodayRef = useRef<(() => void) | null>(null);
  const scrollToMonthRef = useRef<((month: Date) => void) | null>(null);

  // Sync currentMonth when date parameter changes (e.g., navigating from onboarding)
  useEffect(() => {
    if (!isSameMonth(computeMonthFromURL(), currentMonth)) {
      setCurrentMonth(computeMonthFromURL());
    }
  }, [currentMonth, searchParams, computeMonthFromURL]);

  // Monthly calendar navigation handlers
  const handleBackToMultiPlanner = useCallback(() => {
    navigate('/jobs/calendar');
  }, [navigate]);

  const handleMonthSelect = useCallback((month: Date) => {
    setCurrentMonth(month);
    // Use ref to call scroll function in CalendarView
    if (scrollToMonthRef.current) {
      scrollToMonthRef.current(month);
    }
  }, []);

  // Handle month changes from scrolling within CalendarView
  const handleMonthChange = useCallback(
    (month: Date) => {
      // Only update if it's actually different to prevent loops
      if (!isSameMonth(month, currentMonth)) {
        setCurrentMonth(month);
      }
    },
    [currentMonth]
  );

  const handleTodayClick = useCallback(() => {
    const today = startOfMonth(new Date());
    setCurrentMonth(today);
    // Use ref to call scroll function in CalendarView
    if (scrollToTodayRef.current) {
      scrollToTodayRef.current();
    } else {
      console.warn('[Monthly] scrollToTodayRef.current is null!');
    }
  }, []);

  // Job click handler - uses relative navigation for outlet behavior
  const handleJobClick = useCallback(
    (jobId: string) => {
      // Preserve original returnTo when navigating from one job to another
      // This ensures closing only requires one click instead of two
      const currentState = location.state as { returnTo?: string } | null;
      const originalReturnTo = currentState?.returnTo;

      // If we're already in a job view, preserve the original returnTo
      // Otherwise, set returnTo to the current calendar path
      const returnTo = originalReturnTo || `/jobs/calendar/${placeId}`;

      navigate(jobId, { state: { returnTo } });
    },
    [navigate, placeId, location.state]
  );

  return (
    <CalendarLayout
      viewType='calendar'
      calendarViewControls={{
        onBackClick: handleBackToMultiPlanner,
        onMonthSelect: handleMonthSelect,
        onTodayClick: handleTodayClick,
        currentDate: currentMonth,
      }}
    >
      <CalendarViewWithRangeExtension
        placeId={placeId!}
        jobId={jobId}
        currentMonth={currentMonth}
        onJobClick={handleJobClick}
        onMonthChange={handleMonthChange}
        scrollToTodayRef={scrollToTodayRef}
        scrollToMonthRef={scrollToMonthRef}
      />
    </CalendarLayout>
  );
}
